import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Search, MoreVertical, Pencil, Trash2, Building2, MapPin, Bed, Bath, Ruler, ArrowLeft, DoorOpen, Home, LayoutGrid, ImageIcon } from "lucide-react";
import FileUpload from "../components/FileUpload";

const API_URL = process.env.REACT_APP_BACKEND_URL;

var unitTypes = ["room", "apartment", "studio", "office", "storage", "parking"];
var unitStatuses = ["available", "rented", "maintenance", "reserved"];
var facilityTypes = ["bathroom", "kitchen", "sink", "living_room", "laundry", "parking", "garden", "balcony", "storage"];

export default function Properties() {
  var { user, token } = useAuth();
  var { formatPrice } = useCurrency();
  var { t } = useTranslation();
  var navigate = useNavigate();
  var [properties, setProperties] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [editingProperty, setEditingProperty] = useState(null);
  var [selectedProperty, setSelectedProperty] = useState(null);
  var [units, setUnits] = useState([]);
  var [unitsLoading, setUnitsLoading] = useState(false);
  var [unitDialogOpen, setUnitDialogOpen] = useState(false);
  var [editingUnit, setEditingUnit] = useState(null);

  // Property form
  var [formName, setFormName] = useState("");
  var [formType, setFormType] = useState("apartment");
  var [formAddress, setFormAddress] = useState("");
  var [formCity, setFormCity] = useState("");
  var [formCountry, setFormCountry] = useState("");
  var [formDesc, setFormDesc] = useState("");
  var [formBeds, setFormBeds] = useState("");
  var [formBaths, setFormBaths] = useState("");
  var [formArea, setFormArea] = useState("");
  var [formPrice, setFormPrice] = useState("");
  var [formStatus, setFormStatus] = useState("available");
  var [formCategory, setFormCategory] = useState("");

  // Unit form
  var [unitName, setUnitName] = useState("");
  var [unitType, setUnitType] = useState("room");
  var [unitStatus, setUnitStatus] = useState("available");
  var [unitFloor, setUnitFloor] = useState("");
  var [unitArea, setUnitArea] = useState("");
  var [unitRentPrice, setUnitRentPrice] = useState("");
  var [unitRooms, setUnitRooms] = useState("");
  var [unitDesc, setUnitDesc] = useState("");
  var [unitFacilities, setUnitFacilities] = useState([]);

  var [search, setSearch] = useState("");
  var [filterStatus, setFilterStatus] = useState("all");

  var canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  var isAdmin = user?.role === "admin";

  useEffect(function() { fetchProperties(); }, []);

  function resetPropertyForm() {
    setFormName(""); setFormType("apartment"); setFormAddress(""); setFormCity(""); setFormCountry("");
    setFormDesc(""); setFormBeds(""); setFormBaths(""); setFormArea(""); setFormPrice("");
    setFormStatus("available"); setFormCategory("");
  }

  function resetUnitForm() {
    setUnitName(""); setUnitType("room"); setUnitStatus("available");
    setUnitFloor(""); setUnitArea(""); setUnitRentPrice(""); setUnitRooms(""); setUnitDesc(""); setUnitFacilities([]);
  }

  async function fetchProperties() {
    try {
      var response = await fetch(API_URL + "/api/properties", { credentials: "include", headers: token ? { Authorization: "Bearer " + token } : {} });
      if (response.ok) setProperties(await response.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  async function fetchUnits(propertyId) {
    setUnitsLoading(true);
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, { credentials: "include", headers: token ? { Authorization: "Bearer " + token } : {} });
      if (response.ok) setUnits(await response.json());
    } catch (err) { console.error(err); }
    finally { setUnitsLoading(false); }
  }

  async function handlePropertySubmit(e) {
    e.preventDefault();
    var url = editingProperty ? API_URL + "/api/properties/" + editingProperty.property_id : API_URL + "/api/properties";
    var method = editingProperty ? "PUT" : "POST";
    var payload = { name: formName, property_type: formType, address: formAddress, city: formCity, country: formCountry, description: formDesc, bedrooms: formBeds ? parseInt(formBeds) : null, bathrooms: formBaths ? parseInt(formBaths) : null, area_sqm: formArea ? parseFloat(formArea) : null, price: formPrice ? parseFloat(formPrice) : null, status: formStatus, images: [], custom_fields: {}, category: formCategory };
    try {
      var response = await fetch(url, { method: method, headers: { "Content-Type": "application/json", Authorization: token ? "Bearer " + token : "" }, credentials: "include", body: JSON.stringify(payload) });
      if (response.ok) { 
        toast.success(editingProperty ? t("common.success") : t("common.success")); 
        setDialogOpen(false); 
        setEditingProperty(null); 
        resetPropertyForm(); 
        fetchProperties();
        if (selectedProperty && editingProperty) {
          var updated = await response.json();
          setSelectedProperty(updated);
        }
      }
      else { var err = await response.json(); toast.error(err.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
  }

  async function handleUnitSubmit(e) {
    e.preventDefault();
    var url = editingUnit ? API_URL + "/api/units/" + editingUnit.unit_id : API_URL + "/api/units";
    var method = editingUnit ? "PUT" : "POST";
    
    var facilitiesData = [];
    for (var i = 0; i < unitFacilities.length; i++) {
      facilitiesData.push({ name: unitFacilities[i].name, is_private: unitFacilities[i].is_private });
    }
    
    var payload = { 
      property_id: selectedProperty.property_id, 
      name: unitName, 
      unit_type: unitType, 
      status: unitStatus, 
      floor: unitFloor ? parseInt(unitFloor) : null, 
      area_sqm: unitArea ? parseFloat(unitArea) : null,
      rent_price: unitRentPrice ? parseFloat(unitRentPrice) : null,
      num_rooms: unitRooms ? parseInt(unitRooms) : null,
      description: unitDesc,
      facilities: facilitiesData
    };
    
    try {
      var response = await fetch(url, { method: method, headers: { "Content-Type": "application/json", Authorization: token ? "Bearer " + token : "" }, credentials: "include", body: JSON.stringify(payload) });
      if (response.ok) { 
        toast.success(t("common.success")); 
        setUnitDialogOpen(false); 
        setEditingUnit(null); 
        resetUnitForm(); 
        fetchUnits(selectedProperty.property_id);
      }
      else { var err = await response.json(); toast.error(err.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
  }

  function handleEditProperty(p) {
    setEditingProperty(p);
    setFormName(p.name || ""); setFormType(p.property_type || "apartment"); setFormAddress(p.address || "");
    setFormCity(p.city || ""); setFormCountry(p.country || ""); setFormDesc(p.description || "");
    setFormBeds(p.bedrooms ? String(p.bedrooms) : ""); setFormBaths(p.bathrooms ? String(p.bathrooms) : "");
    setFormArea(p.area_sqm ? String(p.area_sqm) : ""); setFormPrice(p.price ? String(p.price) : "");
    setFormStatus(p.status || "available"); setFormCategory(p.category || "");
    setDialogOpen(true);
  }

  function handleEditUnit(unit) {
    setEditingUnit(unit);
    setUnitName(unit.name || "");
    setUnitType(unit.unit_type || "room");
    setUnitStatus(unit.status || "available");
    setUnitFloor(unit.floor ? String(unit.floor) : "");
    setUnitArea(unit.area_sqm ? String(unit.area_sqm) : "");
    setUnitRentPrice(unit.rent_price ? String(unit.rent_price) : "");
    setUnitRooms(unit.num_rooms ? String(unit.num_rooms) : "");
    setUnitDesc(unit.description || "");
    var facilities = unit.facilities || [];
    var facilityState = [];
    for (var i = 0; i < facilities.length; i++) {
      facilityState.push({ name: facilities[i].name, is_private: facilities[i].is_private });
    }
    setUnitFacilities(facilityState);
    setUnitDialogOpen(true);
  }

  async function handleDeleteProperty(id) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/properties/" + id, { method: "DELETE", credentials: "include", headers: token ? { Authorization: "Bearer " + token } : {} });
      if (response.ok) { toast.success(t("common.success")); fetchProperties(); setSelectedProperty(null); } 
      else { toast.error(t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
  }

  async function handleDeleteUnit(unitId) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/units/" + unitId, { method: "DELETE", credentials: "include", headers: token ? { Authorization: "Bearer " + token } : {} });
      if (response.ok) { toast.success(t("common.success")); fetchUnits(selectedProperty.property_id); } 
      else { var err = await response.json(); toast.error(err.detail || t("common.error")); }
    } catch (err) { toast.error(t("common.error")); }
  }

  function handlePropertyClick(p) {
    setSelectedProperty(p);
    fetchUnits(p.property_id);
  }

  function handleBackToList() {
    setSelectedProperty(null);
    setUnits([]);
  }

  function toggleFacility(facilityName) {
    var found = false;
    var newFacilities = [];
    for (var i = 0; i < unitFacilities.length; i++) {
      if (unitFacilities[i].name === facilityName) {
        found = true;
      } else {
        newFacilities.push(unitFacilities[i]);
      }
    }
    if (!found) {
      newFacilities.push({ name: facilityName, is_private: true });
    }
    setUnitFacilities(newFacilities);
  }

  function toggleFacilityPrivate(facilityName) {
    var newFacilities = [];
    for (var i = 0; i < unitFacilities.length; i++) {
      if (unitFacilities[i].name === facilityName) {
        newFacilities.push({ name: facilityName, is_private: !unitFacilities[i].is_private });
      } else {
        newFacilities.push(unitFacilities[i]);
      }
    }
    setUnitFacilities(newFacilities);
  }

  function isFacilitySelected(facilityName) {
    for (var i = 0; i < unitFacilities.length; i++) {
      if (unitFacilities[i].name === facilityName) return true;
    }
    return false;
  }

  function isFacilityPrivate(facilityName) {
    for (var i = 0; i < unitFacilities.length; i++) {
      if (unitFacilities[i].name === facilityName) return unitFacilities[i].is_private;
    }
    return true;
  }

  function renderUnitTypeOptions() {
    var items = [];
    for (var i = 0; i < unitTypes.length; i++) {
      items.push(<SelectItem key={unitTypes[i]} value={unitTypes[i]}>{t("units.types." + unitTypes[i])}</SelectItem>);
    }
    return items;
  }

  function renderUnitStatusOptions() {
    var items = [];
    for (var i = 0; i < unitStatuses.length; i++) {
      items.push(<SelectItem key={unitStatuses[i]} value={unitStatuses[i]}>{t("units.statuses." + unitStatuses[i])}</SelectItem>);
    }
    return items;
  }

  function renderFacilityCheckboxes() {
    var items = [];
    for (var i = 0; i < facilityTypes.length; i++) {
      (function(fac) {
        var selected = isFacilitySelected(fac);
        var isPrivate = isFacilityPrivate(fac);
        items.push(
          <div key={fac} className="flex items-center gap-2 p-2 rounded border bg-muted/30">
            <Checkbox checked={selected} onCheckedChange={function() { toggleFacility(fac); }} id={"fac-" + fac} />
            <label htmlFor={"fac-" + fac} className="text-sm flex-1 cursor-pointer">{t("units.facilityTypes." + fac)}</label>
            {selected && (
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={function() { toggleFacilityPrivate(fac); }}>
                {isPrivate ? t("units.private") : t("units.shared")}
              </Button>
            )}
          </div>
        );
      })(facilityTypes[i]);
    }
    return items;
  }

  function renderFacilityBadges(facilities) {
    if (!facilities || facilities.length === 0) return "-";
    var badges = [];
    for (var i = 0; i < facilities.length; i++) {
      var f = facilities[i];
      badges.push(<Badge key={i} variant="secondary" className="text-xs">{t("units.facilityTypes." + f.name)} {f.is_private ? "" : "(" + t("units.shared") + ")"}</Badge>);
    }
    return <div className="flex flex-wrap gap-1">{badges}</div>;
  }

  function renderUnitRows() {
    var rows = [];
    for (var i = 0; i < units.length; i++) {
      var unit = units[i];
      var unitId = unit.unit_id;
      var unitData = JSON.parse(JSON.stringify(unit));
      rows.push(
        <TableRow key={unitId} data-testid={"unit-row-" + unitId}>
          <TableCell className="font-medium">{unit.name}</TableCell>
          <TableCell><Badge variant="outline">{t("units.types." + unit.unit_type)}</Badge></TableCell>
          <TableCell><Badge className={statusClass(unit.status)}>{t("units.statuses." + unit.status)}</Badge></TableCell>
          <TableCell>{unit.floor || "-"}</TableCell>
          <TableCell>{unit.area_sqm ? unit.area_sqm + " m²" : "-"}</TableCell>
          <TableCell>{unit.rent_price ? formatPrice(unit.rent_price) : "-"}</TableCell>
          <TableCell>{unit.num_rooms || "-"}</TableCell>
          <TableCell>{renderFacilityBadges(unit.facilities)}</TableCell>
          {isAdmin && (
            <TableCell>
              {unit.owner_name ? (
                <div>
                  <div className="text-sm font-medium">{unit.owner_name}</div>
                  <div className="text-xs text-muted-foreground">{unit.owner_email}</div>
                </div>
              ) : "-"}
            </TableCell>
          )}
          {canEdit && (
            <TableCell>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={handleEditUnit.bind(null, unitData)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={handleDeleteUnit.bind(null, unitId)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </TableCell>
          )}
        </TableRow>
      );
    }
    return rows;
  }

  function getFiltered() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < properties.length; i++) {
      var p = properties[i];
      var matchSearch = p.name.toLowerCase().indexOf(searchLower) >= 0 || (p.address && p.address.toLowerCase().indexOf(searchLower) >= 0) || (p.city && p.city.toLowerCase().indexOf(searchLower) >= 0);
      var matchStatus = filterStatus === "all" || p.status === filterStatus;
      if (matchSearch && matchStatus) result.push(p);
    }
    return result;
  }

  function statusClass(s) {
    if (s === "available") return "status-available";
    if (s === "rented") return "status-rented";
    if (s === "maintenance") return "status-maintenance";
    if (s === "reserved") return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
    return "bg-gray-100 text-gray-800";
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  // Detail View
  if (selectedProperty) {
    return (
      <div className="space-y-6" data-testid="property-detail-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBackToList} data-testid="back-to-properties-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">{selectedProperty.name}</h1>
            <div className="flex items-center gap-2 text-muted-foreground mt-1">
              <MapPin className="w-4 h-4" />
              <span>{selectedProperty.address}, {selectedProperty.city}, {selectedProperty.country}</span>
            </div>
          </div>
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon"><MoreVertical className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={function() { navigate("/floor-plan/" + selectedProperty.property_id); }}>
                  <LayoutGrid className="w-4 h-4 mr-2" />{t("floorPlan.viewFloorPlan")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { handleEditProperty(selectedProperty); }}>
                  <Pencil className="w-4 h-4 mr-2" />{t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { handleDeleteProperty(selectedProperty.property_id); }} className="text-destructive">
                  <Trash2 className="w-4 h-4 mr-2" />{t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Property Info Card */}
        <Card className="bento-card">
          <CardContent className="pt-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("common.type")}</p>
                <p className="font-medium mt-1">{t("properties.types." + selectedProperty.property_type)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("common.status")}</p>
                <Badge className={statusClass(selectedProperty.status) + " mt-1"}>{t("properties.statuses." + selectedProperty.status)}</Badge>
              </div>
              {selectedProperty.bedrooms && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("properties.bedrooms")}</p>
                  <p className="font-medium mt-1 flex items-center gap-1"><Bed className="w-4 h-4" />{selectedProperty.bedrooms}</p>
                </div>
              )}
              {selectedProperty.bathrooms && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("properties.bathrooms")}</p>
                  <p className="font-medium mt-1 flex items-center gap-1"><Bath className="w-4 h-4" />{selectedProperty.bathrooms}</p>
                </div>
              )}
              {selectedProperty.area_sqm && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("properties.area")}</p>
                  <p className="font-medium mt-1 flex items-center gap-1"><Ruler className="w-4 h-4" />{selectedProperty.area_sqm} m²</p>
                </div>
              )}
              {selectedProperty.price && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("common.price")}</p>
                  <p className="font-medium mt-1 text-primary">{formatPrice(selectedProperty.price)}</p>
                </div>
              )}
              {isAdmin && selectedProperty.owner_name && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("common.owner")}</p>
                  <p className="font-medium mt-1">{selectedProperty.owner_name}</p>
                  <p className="text-xs text-muted-foreground">{selectedProperty.owner_email}</p>
                </div>
              )}
            </div>
            {selectedProperty.description && (
              <div className="mt-6 pt-6 border-t">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{t("common.description")}</p>
                <p className="text-sm">{selectedProperty.description}</p>
              </div>
            )}
            
            {/* Property Images Section */}
            <div className="mt-6 pt-6 border-t">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                {t("properties.images")}
              </p>
              <FileUpload
                entityType="property"
                entityId={selectedProperty.property_id}
                folder={"properties/" + selectedProperty.property_id}
                accept="image/*"
                multiple={true}
                showList={true}
              />
            </div>
          </CardContent>
        </Card>

        {/* Units Section */}
        <Card className="bento-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="flex items-center gap-2">
              <DoorOpen className="w-5 h-5" />
              {t("units.title")}
              <Badge variant="secondary" className="ml-2">{units.length}</Badge>
            </CardTitle>
            {canEdit && (
              <Dialog open={unitDialogOpen} onOpenChange={function(o) { setUnitDialogOpen(o); if (!o) { setEditingUnit(null); resetUnitForm(); } }}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-unit-btn"><Plus className="w-4 h-4 mr-2" />{t("units.addUnit")}</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editingUnit ? t("units.editUnit") : t("units.addNewUnit")}</DialogTitle></DialogHeader>
                  <form onSubmit={handleUnitSubmit} className="space-y-4">
                    <div>
                      <Label className="form-label">{t("units.unitName")}</Label>
                      <Input value={unitName} onChange={function(e) { setUnitName(e.target.value); }} required placeholder={t("units.unitNamePlaceholder")} data-testid="unit-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="form-label">{t("units.unitType")}</Label>
                        <Select value={unitType} onValueChange={setUnitType}>
                          <SelectTrigger data-testid="unit-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {renderUnitTypeOptions()}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="form-label">{t("common.status")}</Label>
                        <Select value={unitStatus} onValueChange={setUnitStatus}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {renderUnitStatusOptions()}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="form-label">{t("units.floor")}</Label>
                        <Input type="number" value={unitFloor} onChange={function(e) { setUnitFloor(e.target.value); }} />
                      </div>
                      <div>
                        <Label className="form-label">{t("units.size")}</Label>
                        <Input type="number" value={unitArea} onChange={function(e) { setUnitArea(e.target.value); }} placeholder="m²" />
                      </div>
                      <div>
                        <Label className="form-label">{t("units.rentPrice")}</Label>
                        <Input type="number" value={unitRentPrice} onChange={function(e) { setUnitRentPrice(e.target.value); }} placeholder="€" />
                      </div>
                      <div>
                        <Label className="form-label">{t("units.numRooms")}</Label>
                        <Input type="number" value={unitRooms} onChange={function(e) { setUnitRooms(e.target.value); }} min="1" placeholder="1" data-testid="unit-rooms-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="form-label">{t("common.description")}</Label>
                      <Textarea value={unitDesc} onChange={function(e) { setUnitDesc(e.target.value); }} rows={2} />
                    </div>
                    <div>
                      <Label className="form-label">{t("units.facilities")}</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {renderFacilityCheckboxes()}
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={function() { setUnitDialogOpen(false); }}>{t("common.cancel")}</Button>
                      <Button type="submit" data-testid="unit-submit-btn">{editingUnit ? t("common.update") : t("common.create")}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent>
            {unitsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : units.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("common.type")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead>{t("units.floor")}</TableHead>
                    <TableHead>{t("units.size")}</TableHead>
                    <TableHead>{t("units.rentPrice")}</TableHead>
                    <TableHead>{t("units.numRooms")}</TableHead>
                    <TableHead>{t("units.facilities")}</TableHead>
                    {isAdmin && <TableHead>{t("common.owner")}</TableHead>}
                    {canEdit && <TableHead className="w-20">{t("common.actions")}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {renderUnitRows()}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <DoorOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>{t("units.noUnits")}</p>
                {canEdit && (
                  <Button className="mt-4" size="sm" onClick={function() { setUnitDialogOpen(true); }}>
                    <Plus className="w-4 h-4 mr-2" />{t("units.addFirstUnit")}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Property Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={function(o) { setDialogOpen(o); if (!o) { setEditingProperty(null); resetPropertyForm(); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingProperty ? t("properties.editProperty") : t("properties.addNewProperty")}</DialogTitle></DialogHeader>
            <form onSubmit={handlePropertySubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label className="form-label">{t("properties.propertyName")}</Label><Input value={formName} onChange={function(e) { setFormName(e.target.value); }} required data-testid="property-name-input" /></div>
                <div><Label className="form-label">{t("properties.propertyType")}</Label><Select value={formType} onValueChange={setFormType}><SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="house">{t("properties.types.house")}</SelectItem><SelectItem value="apartment">{t("properties.types.apartment")}</SelectItem><SelectItem value="commercial">{t("properties.types.commercial")}</SelectItem><SelectItem value="land">{t("properties.types.land")}</SelectItem></SelectContent></Select></div>
                <div><Label className="form-label">{t("common.status")}</Label><Select value={formStatus} onValueChange={setFormStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="available">{t("properties.statuses.available")}</SelectItem><SelectItem value="rented">{t("properties.statuses.rented")}</SelectItem><SelectItem value="sold">{t("properties.statuses.sold")}</SelectItem><SelectItem value="maintenance">{t("properties.statuses.maintenance")}</SelectItem></SelectContent></Select></div>
                <div className="col-span-2"><Label className="form-label">{t("properties.address")}</Label><Input value={formAddress} onChange={function(e) { setFormAddress(e.target.value); }} required data-testid="property-address-input" /></div>
                <div><Label className="form-label">{t("properties.city")}</Label><Input value={formCity} onChange={function(e) { setFormCity(e.target.value); }} required /></div>
                <div><Label className="form-label">{t("properties.country")}</Label><Input value={formCountry} onChange={function(e) { setFormCountry(e.target.value); }} required /></div>
                <div><Label className="form-label">{t("properties.bedrooms")}</Label><Input type="number" value={formBeds} onChange={function(e) { setFormBeds(e.target.value); }} /></div>
                <div><Label className="form-label">{t("properties.bathrooms")}</Label><Input type="number" value={formBaths} onChange={function(e) { setFormBaths(e.target.value); }} /></div>
                <div><Label className="form-label">{t("properties.area")}</Label><Input type="number" value={formArea} onChange={function(e) { setFormArea(e.target.value); }} /></div>
                <div><Label className="form-label">{t("common.price")}</Label><Input type="number" value={formPrice} onChange={function(e) { setFormPrice(e.target.value); }} /></div>
                <div className="col-span-2"><Label className="form-label">{t("properties.category")}</Label><Input value={formCategory} onChange={function(e) { setFormCategory(e.target.value); }} /></div>
                <div className="col-span-2"><Label className="form-label">{t("common.description")}</Label><Textarea value={formDesc} onChange={function(e) { setFormDesc(e.target.value); }} rows={2} /></div>
              </div>
              <div className="flex justify-end gap-2 pt-4"><Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button><Button type="submit" data-testid="property-submit-btn">{editingProperty ? t("common.update") : t("common.create")}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  var filtered = getFiltered();

  function renderPropertyCard(p) {
    var hasImage = p.images && p.images.length > 0;
    var unitCount = 0; // We'd need to fetch this separately or include in properties response
    return (
      <Card key={p.property_id} className="group hover:shadow-md transition-shadow overflow-hidden cursor-pointer" onClick={function() { handlePropertyClick(p); }} data-testid={"property-card-" + p.property_id}>
        <div className="relative h-40 bg-muted">
          {hasImage ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-12 h-12 text-muted-foreground/30" /></div>}
          <div className="absolute top-2 right-2"><Badge className={statusClass(p.status)}>{t("properties.statuses." + p.status)}</Badge></div>
        </div>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-semibold truncate">{p.name}</h3>
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1"><MapPin className="w-3 h-3" /><span className="truncate">{p.city}, {p.country}</span></div>
            </div>
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={function(e) { e.stopPropagation(); }}>
                  <Button variant="ghost" size="icon" className="shrink-0"><MoreVertical className="w-4 h-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={function(e) { e.stopPropagation(); navigate("/floor-plan/" + p.property_id); }}><LayoutGrid className="w-4 h-4 mr-2" />{t("floorPlan.viewFloorPlan")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={function(e) { e.stopPropagation(); handleEditProperty(p); }}><Pencil className="w-4 h-4 mr-2" />{t("common.edit")}</DropdownMenuItem>
                  <DropdownMenuItem onClick={function(e) { e.stopPropagation(); handleDeleteProperty(p.property_id); }} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" />{t("common.delete")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
            {p.bedrooms && <div className="flex items-center gap-1"><Bed className="w-4 h-4" />{p.bedrooms}</div>}
            {p.bathrooms && <div className="flex items-center gap-1"><Bath className="w-4 h-4" />{p.bathrooms}</div>}
            {p.area_sqm && <div className="flex items-center gap-1"><Ruler className="w-4 h-4" />{p.area_sqm}</div>}
          </div>
          {p.price && <div className="mt-3 text-lg font-bold text-primary">{formatPrice(p.price)}</div>}
          <div className="flex items-center justify-between mt-3">
            <Badge variant="outline">{t("properties.types." + p.property_type)}</Badge>
            <span className="text-xs text-muted-foreground">{t("properties.clickToView")}</span>
          </div>
          {isAdmin && p.owner_name && (
            <div className="mt-3 pt-3 border-t">
              <div className="text-xs text-muted-foreground">{t("common.owner")}</div>
              <div className="text-sm font-medium truncate">{p.owner_name}</div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  function renderPropertyCards() {
    var items = [];
    for (var i = 0; i < filtered.length; i++) {
      items.push(renderPropertyCard(filtered[i]));
    }
    return items;
  }

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h1 className="text-3xl font-bold tracking-tight">{t("properties.title")}</h1><p className="text-muted-foreground mt-1">{t("properties.subtitle")}</p></div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={function(o) { setDialogOpen(o); if (!o) { setEditingProperty(null); resetPropertyForm(); } }}>
            <DialogTrigger asChild><Button data-testid="add-property-btn"><Plus className="w-4 h-4 mr-2" />{t("properties.addProperty")}</Button></DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingProperty ? t("properties.editProperty") : t("properties.addNewProperty")}</DialogTitle></DialogHeader>
              <form onSubmit={handlePropertySubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2"><Label className="form-label">{t("properties.propertyName")}</Label><Input value={formName} onChange={function(e) { setFormName(e.target.value); }} required data-testid="property-name-input" /></div>
                  <div><Label className="form-label">{t("properties.propertyType")}</Label><Select value={formType} onValueChange={setFormType}><SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="house">{t("properties.types.house")}</SelectItem><SelectItem value="apartment">{t("properties.types.apartment")}</SelectItem><SelectItem value="commercial">{t("properties.types.commercial")}</SelectItem><SelectItem value="land">{t("properties.types.land")}</SelectItem></SelectContent></Select></div>
                  <div><Label className="form-label">{t("common.status")}</Label><Select value={formStatus} onValueChange={setFormStatus}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="available">{t("properties.statuses.available")}</SelectItem><SelectItem value="rented">{t("properties.statuses.rented")}</SelectItem><SelectItem value="sold">{t("properties.statuses.sold")}</SelectItem><SelectItem value="maintenance">{t("properties.statuses.maintenance")}</SelectItem></SelectContent></Select></div>
                  <div className="col-span-2"><Label className="form-label">{t("properties.address")}</Label><Input value={formAddress} onChange={function(e) { setFormAddress(e.target.value); }} required data-testid="property-address-input" /></div>
                  <div><Label className="form-label">{t("properties.city")}</Label><Input value={formCity} onChange={function(e) { setFormCity(e.target.value); }} required /></div>
                  <div><Label className="form-label">{t("properties.country")}</Label><Input value={formCountry} onChange={function(e) { setFormCountry(e.target.value); }} required /></div>
                  <div><Label className="form-label">{t("properties.bedrooms")}</Label><Input type="number" value={formBeds} onChange={function(e) { setFormBeds(e.target.value); }} /></div>
                  <div><Label className="form-label">{t("properties.bathrooms")}</Label><Input type="number" value={formBaths} onChange={function(e) { setFormBaths(e.target.value); }} /></div>
                  <div><Label className="form-label">{t("properties.area")}</Label><Input type="number" value={formArea} onChange={function(e) { setFormArea(e.target.value); }} /></div>
                  <div><Label className="form-label">{t("common.price")}</Label><Input type="number" value={formPrice} onChange={function(e) { setFormPrice(e.target.value); }} /></div>
                  <div className="col-span-2"><Label className="form-label">{t("properties.category")}</Label><Input value={formCategory} onChange={function(e) { setFormCategory(e.target.value); }} /></div>
                  <div className="col-span-2"><Label className="form-label">{t("common.description")}</Label><Textarea value={formDesc} onChange={function(e) { setFormDesc(e.target.value); }} rows={2} /></div>
                </div>
                <div className="flex justify-end gap-2 pt-4"><Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button><Button type="submit" data-testid="property-submit-btn">{editingProperty ? t("common.update") : t("common.create")}</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder={t("common.search")} value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" data-testid="property-search-input" /></div>
        <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-40"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{t("common.all")}</SelectItem><SelectItem value="available">{t("properties.statuses.available")}</SelectItem><SelectItem value="rented">{t("properties.statuses.rented")}</SelectItem><SelectItem value="sold">{t("properties.statuses.sold")}</SelectItem><SelectItem value="maintenance">{t("properties.statuses.maintenance")}</SelectItem></SelectContent></Select>
      </div>

      {filtered.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">{renderPropertyCards()}</div>
      ) : (
        <Card className="bento-card"><CardContent className="py-12 text-center text-muted-foreground"><Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>{t("properties.noProperties")}</p>{canEdit && <Button className="mt-4" onClick={function() { setDialogOpen(true); }}>{t("properties.addFirstProperty")}</Button>}</CardContent></Card>
      )}
    </div>
  );
}
